import {useEffect, useRef, useState} from "react";
import {ConferenceStatusType} from "@inpriton/types/conferenceTypes";
import type {ConferenceUserWithStatus, UserLanguagePreferences, UserLiveStatus} from "@inpriton/types/conferenceTypes";

const samePreferences = (a: UserLanguagePreferences, b: UserLanguagePreferences) =>
  (Object.keys(b) as (keyof UserLanguagePreferences)[]).every(key => JSON.stringify(a[key]) === JSON.stringify(b[key]))

const useRoster = () => {
  const [ conferenceStatus, setConferenceStatus ] = useState<ConferenceStatusType>({ started: null, mayStart: false, users: [], admitted: null, conferenceLanguages: [], userPreferences: null, translationType: null })
  const [ users, setUsers ] = useState<ConferenceUserWithStatus[]>([])
  const [ userVolumes, setUserVolumes ] = useState<{ [uuid: string]: number }>({})
  const [ userNames, setUserNames ] = useState<{ [uuid: string]: string }>({})

  // Socket handlers must read this, not `users`: they are bound once per client, and their `users` stays
  // the roster at join, which for a user who waited is only itself (RISKS.md/P1.28).
  const usersRef = useRef(users)
  usersRef.current = users

  useEffect(() => {
    let newVolumes = JSON.parse(JSON.stringify(userVolumes))
    for (const user of users) {
      if (typeof newVolumes[user.uuid] === 'undefined') {
        newVolumes[user.uuid] = 1
      }
    }
    setUserVolumes(newVolumes)
    // Not `userVolumes`: the body replaces it, so listing it loops forever.
  }, [users])

  const onUserUpdate = (user: ConferenceUserWithStatus) => {
    const exists = usersRef.current.some(u => u.uuid === user.uuid)
    if (exists) {
      setUsers(usersRef.current.map(u =>
        u.uuid === user.uuid ? { ...user, status: u.status } : u
      ))
    } else {
      setUsers([...usersRef.current, { ...user }])
    }
  }

  // A moderator's kick. Later rosters from the server omit the user too, so nothing re-adds them.
  const onUserRemoved = (uuid: string) => {
    setUsers(usersRef.current.filter(u => u.uuid !== uuid))
  }

  // A field the push leaves out keeps its value: a waiting user's push carries no uuids
  // (RISKS.md/P1.28).
  const onPreferencesUpdated = (data: Partial<UserLanguagePreferences>) => {
    setConferenceStatus(prev => {
      const existing = prev.userPreferences
      const next: UserLanguagePreferences = {
        spokenLanguage: data.spokenLanguage ?? existing?.spokenLanguage ?? '',
        neededLanguages: data.neededLanguages ?? existing?.neededLanguages ?? [],
        simultanConfig: data.simultanConfig !== undefined ? data.simultanConfig : (existing?.simultanConfig ?? null),
        assignedTranslatorUuids: data.assignedTranslatorUuids ?? existing?.assignedTranslatorUuids ?? [],
        responsibleTranslatorUuids: data.responsibleTranslatorUuids ?? existing?.responsibleTranslatorUuids ?? [],
        missingInterpreters: data.missingInterpreters ?? existing?.missingInterpreters ?? [],
        awaitingInterpreters: data.awaitingInterpreters ?? existing?.awaitingInterpreters ?? [],
        speakersToFilter: data.speakersToFilter ?? existing?.speakersToFilter ?? [],
      }
      // FEATURES.md/F1.3: the server pushes the routing at every join, departure and save in the
      // room, mostly unchanged for this user, and a new object would re-render every tile for nothing.
      return existing && samePreferences(existing, next) ? prev : { ...prev, userPreferences: next }
    })
  }

  // In place on purpose: until the next render, the next event maps this same `usersRef` array,
  // so a fresh entry built here would be lost.
  const onUserStatus = (uuid: string, status: UserLiveStatus) => {
    const user = usersRef.current.find(u => u.uuid === uuid)
    console.log('got status update', user?.name, uuid, status)
    setUsers(usersRef.current.map(user => {
      if (user.uuid === uuid) {
        user.status = status
      }
      return user
    }))
  }

  // Never pruned, so chat history still names a sender after a kick removes them from the roster.
  useEffect(() => {
    setUserNames(prev => {
      let changed = false
      const next = { ...prev }
      for (const u of users) {
        const label = u.name || u.email
        if (label && next[u.uuid] !== label) {
          next[u.uuid] = label
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [users])

  return {
    users, setUsers, usersRef,
    conferenceStatus, setConferenceStatus,
    userVolumes, setUserVolumes,
    userNames,
    onUserUpdate, onUserRemoved, onPreferencesUpdated, onUserStatus,
  }
}

export default useRoster
